import {
  ContentPageConfiguration,
  TemplatePageDefinition,
} from "../../types/customization/template";
import { buildRotationPagesMapping } from "./page";

export interface TemplatePageDefinitionsByCountKey {
  [key: number]: TemplatePageDefinition[];
}
export function layoutsByEditablePicturesCount(
  pageDefinitionKey: string,
  pageDefinitions: TemplatePageDefinition[],
  useFilterType = true
): TemplatePageDefinitionsByCountKey | null {
  const pageDefinition = pageDefinitions.find(({ key }) => key === pageDefinitionKey);
  if (!pageDefinition) {
    return null;
  }

  const pageDefinitionsByEditablePicturesCount: {
    [key: number]: TemplatePageDefinition[];
  } = {};
  const orientationPairs = buildRotationPagesMapping(pageDefinitions);

  pageDefinitions.forEach((definition) => {
    if (
      definition.type !== pageDefinition.type ||
      (useFilterType && definition.filterType != pageDefinition.filterType) ||
      (orientationPairs[pageDefinition.key] &&
        orientationPairs[definition.key] &&
        orientationPairs[pageDefinition.key].which !== orientationPairs[definition.key].which)
    ) {
      return;
    }

    const count = countEditablePicturesInPage(definition);
    if (pageDefinitionsByEditablePicturesCount[count]) {
      pageDefinitionsByEditablePicturesCount[count].push(definition);
    } else {
      pageDefinitionsByEditablePicturesCount[count] = [definition];
    }
  });

  return pageDefinitionsByEditablePicturesCount;
}

export function countEditablePicturesInPage(pageDefinition: TemplatePageDefinition): number {
  return pageDefinition.elementDefinitions.reduce(
    (count: number, definition) => (definition.type === "editable-picture" ? count + 1 : count),
    0
  );
}

export interface MinMax {
  min: number;
  max: number;
}
export function findMinMaxEditablePicturesInPage(
  pageDefinitionKey: string,
  pageDefinitions: TemplatePageDefinition[]
): MinMax {
  let max = 0;
  let min = 0;

  const pageDefinition = pageDefinitions.find(({ key }) => key === pageDefinitionKey);
  if (!pageDefinition) {
    return { max, min };
  }

  pageDefinitions.forEach((definition) => {
    if (
      (definition.type !== pageDefinition.type &&
        !(
          !pageDefinition.rotationFromPageDefinitionKey ||
          pageDefinition.rotationFromPageDefinitionKey === definition.rotationFromPageDefinitionKey
        )) ||
      definition.filterType !== pageDefinition.filterType
    ) {
      return;
    }

    const count = countEditablePicturesInPage(definition);
    if (count > max) {
      max = count;
    }
    if (count < min || min === 0) {
      min = count;
    }
  });

  return { max, min };
}

export function findMinMaxEditablePictures(pageDefinitions: TemplatePageDefinition[]): MinMax {
  let max = 0;
  let min = 0;

  pageDefinitions.forEach((definition) => {
    const count = countEditablePicturesInPage(definition);
    if (count > max) {
      max = count;
    }
    if (count < min || min === 0) {
      min = count;
    }
  });

  return { max, min };
}

export function hasLayoutWithMorePictures(
  pageDefinitionKey: string,
  pageDefinitions: TemplatePageDefinition[]
): boolean {
  const pageDefinition = pageDefinitions.find(({ key }) => key === pageDefinitionKey);
  if (!pageDefinition) {
    return false;
  }

  const editablePicturesCount = countEditablePicturesInPage(pageDefinition);
  const pageWithMoreEditablePictures = pageDefinitions.findIndex((definition) => {
    const picturesCount = countEditablePicturesInPage(definition);
    if (
      definition.type === pageDefinition.type &&
      definition.filterType === pageDefinition.filterType &&
      (!pageDefinition.rotationFromPageDefinitionKey ||
        pageDefinition.rotationFromPageDefinitionKey ===
          definition.rotationFromPageDefinitionKey) &&
      picturesCount > editablePicturesCount
    ) {
      return true;
    }
    return false;
  });

  return pageWithMoreEditablePictures > -1;
}

export function getNextLayoutWithMorePictures(
  pageDefinitionKey: string,
  pageDefinitions: TemplatePageDefinition[],
  selectedPicturesCount?: number
): TemplatePageDefinition | null {
  const pageDefinition = pageDefinitions.find(({ key }) => key === pageDefinitionKey);
  if (!pageDefinition) {
    return null;
  }

  let currentEditablePicturesCount = countEditablePicturesInPage(pageDefinition);
  const layoutsByCount = layoutsByEditablePicturesCount(pageDefinitionKey, pageDefinitions);
  if (layoutsByCount === null) {
    return null;
  }

  const picturesCounts = Object.keys(layoutsByCount);
  picturesCounts.sort((a, b) => +a - +b);
  const maxPicturesCount = +picturesCounts[picturesCounts.length - 1];
  if (maxPicturesCount <= currentEditablePicturesCount) {
    return null;
  }
  if (selectedPicturesCount) {
    currentEditablePicturesCount += selectedPicturesCount;
  } else {
    currentEditablePicturesCount += 1;
  }
  if (currentEditablePicturesCount > maxPicturesCount) {
    currentEditablePicturesCount = maxPicturesCount;
  }

  const nextPicturesCount = picturesCounts.find((count) => +count >= currentEditablePicturesCount);
  if (!nextPicturesCount) {
    return null;
  }

  return layoutsByCount[+nextPicturesCount][0];
}

export function findPageDefinitionsForAdditionalPages(
  pageDefinitions: TemplatePageDefinition[],
  contentPageConfiguration: ContentPageConfiguration
): TemplatePageDefinition[] {
  return pageDefinitions.filter(
    (definition) => definition.filterType === contentPageConfiguration.addNext.filterType
  );
}
